import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * WorkOrder : famille "ordre de travail" (travail technique concret).
 *
 * Travail à réaliser sur un bien, un lieu, un animal ou chez un client :
 * diagnostic, pièces consommées, main d'oeuvre, clôture, souvent facturable.
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas d'intervention concrète ("réparation vélo", "visite
 * vétérinaire") : il connaît la famille générique `workOrder`. Le vocabulaire
 * métier est porté par le champ `type` (chaîne libre déclarée par l'app), et
 * l'état du cycle de vie par `status`.
 *
 * Sujet du travail (subjectRef) : polymorphe, un seul des trois ids est en
 * général renseigné selon le contexte (`assetId` un bien, `custodyId` un bien
 * confié, `placeId` un lieu). Tous optionnels : un ordre peut aussi ne viser
 * qu'un `contactId` (intervention chez le client).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 * Les liens timesheet et stock-movement NE vivent PAS ici : c'est l'autre
 * entité qui porte un `workOrderId` (le temps saisi et les mouvements de stock
 * pointent l'ordre). Côté ordre, `partLines` et `laborMinutes` en gardent le
 * résumé dénormalisé.
 *
 * Accès : v0 reste org-wide (même doctrine que asset). Rien de sensible n'est
 * signalé sur cette famille. L'accès fin par rôle (atelier, planning) viendra
 * après v0, hors de ce schéma.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille workOrder (hors champs standard du socle). */
export const workOrderCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reparation", "visite"). */
  type: z.string().min(1),
  /** Sujet : bien visé (référence `asset`), optionnel. */
  assetId: z.string().min(1).optional(),
  /** Sujet : bien confié visé (référence `custody`), optionnel. */
  custodyId: z.string().min(1).optional(),
  /** Sujet : lieu visé (référence `place`), optionnel. */
  placeId: z.string().min(1).optional(),
  /** Client concerné (référence `contact`), optionnel. */
  contactId: z.string().min(1).optional(),
  /** État courant, chaîne libre pilotée par l'app (ex. "ouvert", "en_cours", "clos"). Optionnel. */
  status: z.string().min(1).optional(),
  /** Diagnostic technique rédigé par l'intervenant, optionnel. */
  diagnosis: z.string().min(1).optional(),
  /** Pièces consommées (désignation, quantité, prix...). Liste structurée, optionnel. */
  partLines: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Main d'oeuvre cumulée en minutes, optionnel. */
  laborMinutes: z.number().int().optional(),
  /** Intervenant assigné (référence `worker`), optionnel. */
  assignedWorkerId: z.string().min(1).optional(),
  /** Date de clôture effective du travail, optionnel. */
  completedAt: z.date().optional(),
  /** Commande de rattachement si le travail en découle (référence `order`), optionnel. */
  orderId: z.string().min(1).optional(),
  /** Rendez-vous qui a planifié le travail (référence `booking`), optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Ticket à l'origine du travail (référence `ticket`), optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Inspection à l'origine du travail (référence `inspection`), optionnel. */
  inspectionId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille workOrder (champs standard + champs métier). */
export const WorkOrderSchema = z.object({
  ...baseEntityFields,
  ...workOrderCoreFields,
});

/** Type d'un workOrder. */
export type WorkOrder = z.infer<typeof WorkOrderSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const WorkOrderCreateInputSchema = z.object({
  ...workOrderCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un workOrder. */
export type WorkOrderCreateInput = z.infer<typeof WorkOrderCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const WorkOrderUpdateInputSchema = z
  .object({ ...workOrderCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un workOrder. */
export type WorkOrderUpdateInput = z.infer<typeof WorkOrderUpdateInputSchema>;
